/**
 * Admin: Integration Program Manager
 * Upload videos, manage day content, create discount codes
 */

import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Upload, Check, X, Video, Loader2, Plus, Tag } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminIntegrationProgram = () => {
  const [days, setDays] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newCode, setNewCode] = useState("");
  const [newCodeDiscount, setNewCodeDiscount] = useState(100);
  const [editDay, setEditDay] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editSubtitle, setEditSubtitle] = useState("");
  const [editReflection, setEditReflection] = useState("");

  const token = localStorage.getItem("reclaim_token");
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    try {
      const [daysRes, codesRes] = await Promise.all([
        fetch(`${API_URL}/api/integration-program/admin/days`, { headers }),
        fetch(`${API_URL}/api/integration-program/admin/discount-codes`, { headers }),
      ]);
      const daysData = await daysRes.json();
      const codesData = await codesRes.json();
      setDays(daysData.days || []);
      setCodes(codesData.codes || []);
    } catch (err) {
      console.error("Load error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleVideoUpload = async (dayNumber, file) => {
    setUploading(dayNumber);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", `Day ${dayNumber}`);

    try {
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
      };

      await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText));
          else reject(new Error(xhr.statusText));
        };
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.open("POST", `${API_URL}/api/integration-program/admin/days/${dayNumber}/video`);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.send(formData);
      });

      loadData();
    } catch (err) {
      console.error("Upload error:", err);
      alert("Upload failed: " + err.message);
    } finally {
      setUploading(null);
      setUploadProgress(0);
    }
  };

  const handleSaveDay = async (dayNumber) => {
    try {
      await fetch(`${API_URL}/api/integration-program/admin/days/${dayNumber}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          title: editTitle,
          subtitle: editSubtitle,
          reflection_prompt: editReflection,
        }),
      });
      setEditDay(null);
      loadData();
    } catch (err) {
      console.error("Save error:", err);
    }
  };

  const handleCreateCode = async () => {
    if (!newCode.trim()) return;
    try {
      await fetch(`${API_URL}/api/integration-program/admin/discount-codes`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ code: newCode.trim(), discount_percent: newCodeDiscount }),
      });
      setNewCode("");
      loadData();
    } catch (err) {
      console.error("Code error:", err);
    }
  };

  if (loading) return <div className="p-8 text-white/50">Loading...</div>;

  const daysWithVideo = days.filter(d => d.has_video).length;

  return (
    <div className="min-h-screen p-6" style={{ background: "#0c0c0c" }}>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
          Integration Program
        </h1>
        <p className="text-sm text-white/30 mb-8">{daysWithVideo}/21 days with video</p>

        {/* Discount Codes */}
        <div className="mb-10 p-5 rounded-2xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="flex items-center gap-2 mb-4">
            <Tag size={16} className="text-amber-400/50" />
            <h2 className="text-sm text-white/70">Discount Codes</h2>
          </div>

          <div className="flex gap-2 mb-4">
            <input
              value={newCode}
              onChange={(e) => setNewCode(e.target.value.toUpperCase())}
              placeholder="CODE NAME"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-amber-500/30"
              data-testid="discount-code-input"
            />
            <input
              type="number"
              value={newCodeDiscount}
              onChange={(e) => setNewCodeDiscount(parseInt(e.target.value))}
              className="w-20 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white text-center focus:outline-none focus:border-amber-500/30"
              placeholder="%"
            />
            <button
              onClick={handleCreateCode}
              className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 text-sm hover:bg-amber-500/30 transition-colors"
              data-testid="create-code-btn"
            >
              <Plus size={16} />
            </button>
          </div>

          {codes.length > 0 && (
            <div className="space-y-1">
              {codes.map((c) => (
                <div key={c.code} className="flex items-center justify-between py-1.5 px-2 rounded text-xs">
                  <span className="text-amber-400/70 font-mono">{c.code}</span>
                  <span className="text-white/30">{c.discount_percent}% off • {c.uses || 0} used</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Days Grid */}
        <div className="space-y-3">
          {days.map((day) => (
            <motion.div
              key={day.day_number}
              className="rounded-xl p-4 transition-all"
              style={{
                background: day.has_video ? "rgba(34,85,34,0.06)" : "rgba(255,255,255,0.02)",
                border: `1px solid ${day.has_video ? "rgba(34,85,34,0.15)" : "rgba(255,255,255,0.05)"}`,
              }}
            >
              <div className="flex items-center gap-4">
                {/* Day number */}
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-mono"
                  style={{ background: day.has_video ? "rgba(34,85,34,0.15)" : "rgba(255,255,255,0.05)", color: day.has_video ? "rgba(120,180,120,0.7)" : "rgba(255,255,255,0.2)" }}>
                  {day.day_number}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white/70">{day.title || `Day ${day.day_number}`}</p>
                  {day.has_video && (
                    <p className="text-[11px] text-white/25 flex items-center gap-1">
                      <Video size={10} /> {day.video_filename} ({day.video_size_mb}MB)
                    </p>
                  )}
                </div>

                {/* Status indicators */}
                <div className="flex items-center gap-2">
                  {day.has_video && <Check size={14} className="text-green-500/50" />}
                </div>

                {/* Upload button */}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && handleVideoUpload(day.day_number, e.target.files[0])}
                    disabled={uploading !== null}
                  />
                  {uploading === day.day_number ? (
                    <div className="flex items-center gap-2">
                      <Loader2 size={16} className="text-amber-400 animate-spin" />
                      <span className="text-xs text-amber-400">{uploadProgress}%</span>
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/5 hover:bg-white/10 transition-colors"
                      data-testid={`upload-day-${day.day_number}`}>
                      <Upload size={14} className="text-white/30" />
                    </div>
                  )}
                </label>

                {/* Edit button */}
                <button
                  onClick={() => {
                    if (editDay === day.day_number) {
                      setEditDay(null);
                    } else {
                      setEditDay(day.day_number);
                      setEditTitle(day.title || `Day ${day.day_number}`);
                      setEditSubtitle(day.subtitle || "");
                      setEditReflection(day.has_reflection ? "" : "");
                    }
                  }}
                  className="text-xs text-white/20 hover:text-white/40 transition-colors"
                >
                  Edit
                </button>
              </div>

              {/* Edit panel */}
              {editDay === day.day_number && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  className="mt-4 space-y-3"
                >
                  <input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Day title"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none"
                  />
                  <input
                    value={editSubtitle}
                    onChange={(e) => setEditSubtitle(e.target.value)}
                    placeholder="Subtitle"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none"
                  />
                  <textarea
                    value={editReflection}
                    onChange={(e) => setEditReflection(e.target.value)}
                    placeholder="Reflection prompt"
                    rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSaveDay(day.day_number)}
                      className="px-4 py-1.5 rounded-lg bg-green-500/20 text-green-400 text-xs"
                    >
                      Save
                    </button>
                    <button onClick={() => setEditDay(null)} className="px-4 py-1.5 rounded-lg bg-white/5 text-white/30 text-xs">
                      Cancel
                    </button>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminIntegrationProgram;
